import { useState } from "react";
import { LayoutRow } from "./types";
import { ViewportMode } from "./types";
import { buildGridTemplateColumns, formatWidthLabel } from "./layoutUtils";
import { PreviewViewportToggle } from "./PreviewViewportToggle";
import { SECTION_TYPE_LABELS, SectionType } from "consts/sectionTypes";

interface LayoutPreviewProps {
  rows: LayoutRow[];
}

const SECTION_COLORS: Record<string, string> = {
  "course-hero": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  "course-enrollment": "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  "course-tabs": "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  "course-sticky-bar": "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};

const DEFAULT_COLOR = "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300";

export const LayoutPreview = ({ rows }: LayoutPreviewProps) => {
  const [viewport, setViewport] = useState<ViewportMode>("desktop");

  const isMobile = viewport === "mobile";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Podgląd struktury
        </span>
        <PreviewViewportToggle mode={viewport} onChange={setViewport} />
      </div>

      <div
        className={`mx-auto rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900 ${
          isMobile ? "max-w-[375px]" : "w-full"
        }`}
        style={{ transition: "max-width 0.3s ease" }}
      >
        <div className="space-y-3">
          {rows.map((row) => {
            if (isMobile) {
              // Mobile: stack columns, respect visibility and order
              const visibleColumns = row.columns
                .filter((col) => col.mobileConfig.visible)
                .sort((a, b) => (a.mobileConfig.order ?? 99) - (b.mobileConfig.order ?? 99));

              if (visibleColumns.length === 0) return null;

              return (
                <div
                  key={row.id}
                  className="space-y-2"
                  style={{
                    marginTop: row.marginTop ? `${row.marginTop}px` : undefined,
                    marginBottom: row.marginBottom ? `${row.marginBottom}px` : undefined,
                  }}
                >
                  {visibleColumns.map((col) => (
                    <div key={col.id} className="space-y-1">
                      {col.sections.map((section, i) => {
                        const label =
                          SECTION_TYPE_LABELS[section.type as SectionType] ?? section.type;
                        const colorClass = SECTION_COLORS[section.type] ?? DEFAULT_COLOR;
                        return (
                          <div
                            key={i}
                            className={`rounded-md px-3 py-2 text-xs font-medium ${colorClass}`}
                          >
                            {label}
                            {section.name && (
                              <span className="ml-1 opacity-60">({section.name})</span>
                            )}
                          </div>
                        );
                      })}
                      {col.sections.length === 0 && (
                        <div className="rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-400 dark:border-slate-600">
                          (pusta kolumna)
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            }

            // Desktop: grid layout
            return (
              <div
                key={row.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: buildGridTemplateColumns(row.columns),
                  gap: `${row.gap ?? 24}px`,
                  marginTop: row.marginTop ? `${row.marginTop}px` : undefined,
                  marginBottom: row.marginBottom ? `${row.marginBottom}px` : undefined,
                }}
              >
                {row.columns.map((col) => (
                  <div
                    key={col.id}
                    className="space-y-1"
                    style={{
                      marginLeft: col.margin?.lg?.left ? `${col.margin.lg.left}px` : undefined,
                      marginRight: col.margin?.lg?.right ? `${col.margin.lg.right}px` : undefined,
                    }}
                  >
                    <div className="mb-1 text-[10px] text-slate-400">
                      {formatWidthLabel(col.width)}
                    </div>
                    {col.sections.map((section, i) => {
                      const label =
                        SECTION_TYPE_LABELS[section.type as SectionType] ?? section.type;
                      const colorClass = SECTION_COLORS[section.type] ?? DEFAULT_COLOR;
                      return (
                        <div
                          key={i}
                          className={`rounded-md px-3 py-2 text-xs font-medium ${colorClass}`}
                        >
                          {label}
                          {section.name && (
                            <span className="ml-1 opacity-60">({section.name})</span>
                          )}
                        </div>
                      );
                    })}
                    {col.sections.length === 0 && (
                      <div className="rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-400 dark:border-slate-600">
                        (pusta kolumna)
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}

          {rows.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-400">Brak wierszy do wyświetlenia</p>
          )}
        </div>
      </div>
    </div>
  );
};
